/* eslint-disable camelcase */
/* eslint-disable linebreak-style */
/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Icon from 'base-ui/dist/Icon';
import NavLink from 'components/NavLink';
import { useDispatch } from 'react-redux';
import useEventListener from 'root/hooks/useEventListener';

import useDeviceScreen from 'root/hooks/useDeviceScreen';
import { intl } from 'components/LocaleProvider';
import { useAmp } from 'next/amp';
import dynamic from 'next/dynamic';
import throttle from 'base-ui/dist/underline/throttle';
import cs from 'classnames';
import isBrowser from 'utils/isBrowser';
import { addBookmark, deleteBookmark } from '../../state/slices/bookmarks';
import messages from './messages';

const CopyLinkButton = dynamic(() => import('components/ShareButtons/CopyLinkButton'));

const getConfig = require('next/config').default;

const { publicRuntimeConfig = {} } = getConfig ? getConfig() : {};

const facebookAppId = publicRuntimeConfig ? (publicRuntimeConfig.fb_app_id || publicRuntimeConfig.FACEBOOK_APP_ID) : process.env.FACEBOOK_APP_ID;

const ShareButtons = ({ post, showIn, isAuthenticated, bookmarks, handleBookmarks }) => {
  const { isMobile } = useDeviceScreen();
  const [show, setShow] = useState(useAmp());
  const [maxY, setMaxY] = useState(0);
  const [iniY, setIniY] = useState(0);
  const [lastY, setLastY] = useState(0);
  const [isBookmark, setIsBookmark] = useState(false);
  const [hide, setHide] = useState(false);

  const openWindow = (link, title) => {
    const mWidth = 500;
    const mHeight = 300;
    const pLeft = window.screen.width / 2 - mWidth / 2;
    const pTop = window.screen.height / 2 - mHeight / 2;
    const features = `menubar=no,toolbar=no,resizable=yes,scrollbars=yes,width=${mWidth},height=${mHeight},top=${pTop},left=${pLeft}`;
    window.open(link, title, features);
  };

  useEffect(() => {
    const result = bookmarks.find((item) => item === post.id);
    setIsBookmark(result !== undefined);
  }, [bookmarks, post]);

  const dispatch = useDispatch();

  const handleBookmarkClick = useCallback((e) => {
    e.preventDefault();
    if (isBookmark === false) {
      dispatch(addBookmark(post));
      handleBookmarks([...bookmarks, post.id]);
    } else if (isBookmark === true) {
      dispatch(deleteBookmark(post.id));
      handleBookmarks(bookmarks.filter((item) => item !== post.id));
    }
  }, [bookmarks, isBookmark, post]);

  const BookmarkButton = () => (isAuthenticated && (
    <a
      href="."
      className={isBookmark ? 'pink-bg share-btn white-color' : 'regent-grey-bg share-btn dark-grey-color'}
      onClick={handleBookmarkClick}
    >
      <i className={isBookmark ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
    </a>
  ));

  const FacebookButton = () => {
    const base = 'https://www.facebook.com/sharer/sharer.php';
    const link = `${base}?u=${post.link}&t=${post.title}`;

    return (
      <>
        {useAmp() ? (
          <amp-social-share
            id="facebook-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'Facebook',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            type="facebook"
            width="45"
            height="45"
            data-param-app_id={facebookAppId}
            data-param-quote={post.title}
          >
          </amp-social-share>
        ) : (
          <a
            href="#"
            id="facebook-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'Facebook',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            className={
              isMobile && post.previous_post
                ? 'facebook-bg share-btn white-color'
                : 'facebook-bg white-color'
            }
            onClick={(e) => {
              e.preventDefault();
              openWindow(link, 'Facebook');
            }}
          >
            <Icon className="icon facebook-shared" prefix="fab" name="facebook" />
          </a>
        )}
      </>
    );
  };

  const TwitterButton = () => {
    const base = 'https://twitter.com/intent/tweet';
    const link = `${base}?text=${post.title} - ${post.link}`;

    return (
      <>
        {useAmp() ? (
          <amp-social-share
            id="twitter-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'Twitter',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            type="twitter"
            width="45"
            height="45"
          >
          </amp-social-share>
        ) : (
          <a
            href="#"
            id="twitter-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'Twitter',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            className={
              isMobile && post.previous_post
                ? 'twitter-bg share-btn white-color'
                : 'twitter-bg white-color'
            }
            onClick={(e) => {
              e.preventDefault();
              openWindow(link, 'Twitter');
            }}
          >
            <Icon className="icon twitter-shared" prefix="fab" name="twitter" />
          </a>
        )}
      </>
    );
  };

  const PinterestButton = () => {
    const base = 'https://br.pinterest.com/pin/create/button';
    const link = `${base}?url=${post.link}&media=${post.metas['og:image']}&description=${post.link}`;

    return (
      <>
        {useAmp() ? (
          <amp-social-share
            id="pinterest-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'Pinterest',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            type="pinterest"
            width="45"
            height="45"
            data-param-media={post.metas['og:image']}
          >
          </amp-social-share>
        ) : (
          <a
            href="#"
            id="pinterest-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'Pinterest',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            className={
              isMobile && post.previous_post
                ? 'pinterest-bg share-btn white-color'
                : 'pinterest-bg white-color'
            }
            onClick={(e) => {
              e.preventDefault();
              openWindow(link, 'Pinterest');
            }}
          >
            <Icon className="icon pinterest-shared" prefix="fab" name="pinterest" />
          </a>
        )}
      </>
    );
  };

  const WhatsappButton = () => {
    const base = 'https://wa.me';
    const link = `${base}?text=${post.title} - ${post.link}`;

    return (
      <>
        {useAmp() ? (
          <amp-social-share
            id="whatsapp-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'WhatsApp',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            type="whatsapp"
            width="45"
            height="45"
          >
          </amp-social-share>
        ) : (
          <a
            href="#"
            id="whatsapp-shared"
            data-with-google-events="true"
            data-google-events={JSON.stringify({
              type: 'social',
              data: {
                socialNetwork: 'WhatsApp',
                socialAction: 'share',
                socialTarget: `${post.link}`
              }
            })}
            className={
              post.previous_post
                ? 'whatsapp-bg share-btn white-color'
                : 'whatsapp-bg white-color'
            }
            onClick={(e) => {
              e.preventDefault();
              openWindow(link, 'WhatsApp');
            }}
          >
            <Icon className="icon whatsapp-shared" prefix="fab" name="whatsapp" />
          </a>
        )}
      </>
    );
  };

  const NextButton = () => {
    if (post.previous_post && (isMobile || useAmp())) {
      return (
        <>
          <NavLink
            basePath={publicRuntimeConfig.PUBLIC_URL}
            as={`${post?.previous_post?.permalink}`}
            to={`/article?permalink=${post.previous_post.permalink}`}
            routerStrategy={'next'}
            className="grey-bg next-article titles-color hidden-lg next-shared"
            id={'next-shared'}
            dataWithGoogleEvents="true"
            dataGoogleEvents={JSON.stringify({
              data: {
                action: 'click',
                category: 'Next article',
                label: `${post.link}`
              }
            })}
          >
            {intl.formatMessage(messages.nextButton)}
            {' '}
            <Icon className="icon next-shared" prefix="fas" name="chevron-right" />
          </NavLink>
        </>
      );
    }

    return null;
  };

  const GerericSharedButton = () => {
    if (useAmp()) {
      return (
        <amp-social-share
          type="system"
          width="45"
          height="45"
        >
        </amp-social-share>
      );
    }

    const shareMobile = (event) => {
      event.preventDefault();
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: post.link
      });
    };

    return (
      <a
        href="#"
        className="mediumgray-bg share-btn white-color"
        onClick={(e) => shareMobile(e)}
      >
        <Icon prefix="fas" name="share-alt" />
      </a>
    );
  };

  const updateHide = () => {
    // eslint-disable-next-line prefer-destructuring
    const scrollTop = document.documentElement.scrollTop;
    const initPosition = document.getElementById(showIn).getBoundingClientRect();

    if (scrollTop - 120 >= initPosition.top) {
      setHide(false);
    } else {
      setHide(true);
    }
  };

  useEffect(() => {
    if (isBrowser()) {
      const mainPost = document.getElementsByClassName('main-post');
      setMaxY(mainPost && mainPost[0] ? mainPost[0].offsetHeight : 12000);
      setIniY(window.screen.height);

      const handleScroll = throttle(() => {
        const actualY = window.scrollY;
        if (actualY > maxY || actualY < lastY) {
          setShow(false);
        } else if (actualY > (iniY / 2) && !show) {
          setShow(true);
        }
        setLastY(actualY);
      }, 100);

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }

    return () => null;
  }, [maxY, show, iniY, lastY]);

  useEffect(() => {
    if (isBrowser() && showIn) {
      updateHide();
      useEventListener('scroll', () => updateHide());
    }
  }, [isBrowser()]);

  return !hide ? (
    <div
      className={cs('share floating', {
        'is-active': show
      })}
    >
      <div className="buttons">
        <BookmarkButton />
        <FacebookButton />
        <TwitterButton />
        <PinterestButton />
        <WhatsappButton />
        {useAmp() || (typeof navigator !== 'undefined' && navigator.share) ? (
          <GerericSharedButton />
        ) : (
          <CopyLinkButton post={post} />
        )}
        <NextButton />
      </div>
    </div>
  ) : null;
};

ShareButtons.propTypes = {
  post: PropTypes.object.isRequired,
  isAuthenticated: PropTypes.bool,
  bookmarks: PropTypes.array,
  handleBookmarks: PropTypes.func
};

ShareButtons.defaultProps = {
  bookmarks: [],
  isAuthenticated: false,
};

export default ShareButtons;
